import { useEffect, useRef, useState } from "react";
import { Camera, X } from "@phosphor-icons/react";

/**
 * Detecta se o dispositivo é smartphone. Diferencia tablet e desktop.
 * Regras:
 *  - Tem touch + user-agent Android/iPhone (não iPad) + largura menor que 900px
 *  - iPad tem user-agent modificado no iOS 13+; ignoramos para não abrir câmera em telas grandes
 */
export function isMobileDevice() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isAndroidPhone = /Android/i.test(ua) && /Mobile/i.test(ua);
  const isIPhone = /iPhone|iPod/i.test(ua);
  const width = typeof window !== "undefined" ? window.innerWidth : 1024;
  return (isAndroidPhone || isIPhone) && width < 900;
}

/**
 * Botão de câmera para leitura de código de barras (aparece só em smartphones).
 * Ao clicar, abre um modal fullscreen que ativa a câmera e escaneia códigos
 * via html5-qrcode. Ao detectar, chama `onResult(codigo)` e fecha o modal.
 *
 * Props:
 *   onResult (fn)   — callback com o texto lido
 *   className       — classes extras no botão
 *   size            — tamanho do ícone (default 18)
 */
export default function BarcodeCameraButton({ onResult, className = "", size = 18 }) {
  const [isMobile, setIsMobile] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setIsMobile(isMobileDevice());
  }, []);

  if (!isMobile) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`flex items-center justify-center border border-black bg-white text-black hover:bg-black hover:text-white transition-colors px-2 py-2 ${className}`}
        title="Escanear código com a câmera"
        data-testid="barcode-camera-btn"
        aria-label="Escanear código com a câmera"
      >
        <Camera size={size} weight="bold" />
      </button>
      {open && (
        <BarcodeCameraModal
          onClose={() => setOpen(false)}
          onResult={(txt) => { setOpen(false); onResult && onResult(txt); }}
        />
      )}
    </>
  );
}

function BarcodeCameraModal({ onClose, onResult }) {
  const containerId = "barcode-scanner-container";
  const scannerRef = useRef(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let stopped = false;
    let scanner = null;

    async function start() {
      try {
        const { Html5Qrcode, Html5QrcodeSupportedFormats } = await import("html5-qrcode");
        if (stopped) return;
        scanner = new Html5Qrcode(containerId, {
          formatsToSupport: [
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.CODE_39,
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.EAN_8,
            Html5QrcodeSupportedFormats.UPC_A,
            Html5QrcodeSupportedFormats.UPC_E,
            Html5QrcodeSupportedFormats.ITF,
            Html5QrcodeSupportedFormats.QR_CODE,
            Html5QrcodeSupportedFormats.CODABAR,
          ],
          verbose: false,
        });
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" }, // câmera traseira
          {
            fps: 12,
            qrbox: (viewportWidth, viewportHeight) => {
              const minEdge = Math.min(viewportWidth, viewportHeight);
              const box = Math.floor(minEdge * 0.8);
              return { width: box, height: Math.floor(box * 0.5) };
            },
            aspectRatio: window.innerHeight / window.innerWidth,
          },
          (decodedText) => {
            if (!decodedText) return;
            // Para o scan imediatamente para evitar leituras duplicadas
            try { scanner.pause(true); } catch (err) { console.warn("Scanner pause:", err); }
            onResult(decodedText.trim());
          },
          () => { /* onScanFailure — silenciar, é chamado a cada frame */ }
        );
      } catch (e) {
        setError(e?.message || String(e));
      }
    }

    start();

    return () => {
      stopped = true;
      const s = scannerRef.current;
      if (s) {
        try {
          if (s.isScanning) s.stop().then(() => { try { s.clear(); } catch (err) { console.warn("Scanner clear (after stop):", err); } }).catch((err) => console.warn("Scanner stop:", err));
          else { try { s.clear(); } catch (err) { console.warn("Scanner clear:", err); } }
        } catch (err) { console.warn("Scanner teardown:", err); }
      }
    };
  }, [onResult]);

  return (
    <div
      className="fixed inset-0 z-[60] bg-black flex flex-col"
      role="dialog"
      aria-modal="true"
      data-testid="barcode-camera-modal"
    >
      <div className="flex items-center justify-between px-4 py-3 bg-black text-white">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/60">Leitor</div>
          <div className="text-sm font-bold">Aponte a câmera para o código</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-2 hover:bg-white/10"
          data-testid="barcode-camera-close"
          aria-label="Fechar câmera"
        >
          <X size={22} />
        </button>
      </div>
      <div className="relative flex-1 min-h-0 bg-black">
        <div id={containerId} className="w-full h-full" />
        {error && (
          <div className="absolute inset-0 flex items-center justify-center text-white text-center px-6">
            <div>
              <div className="font-bold mb-2">Não foi possível abrir a câmera</div>
              <div className="text-sm opacity-80 mb-4">{error}</div>
              <button className="btn btn-ghost !text-white !border-white" onClick={onClose}>Fechar</button>
            </div>
          </div>
        )}
      </div>
      <div className="bg-black text-white text-center px-6 py-3 text-xs opacity-80">
        Enquadre o código de barras dentro da faixa
      </div>
    </div>
  );
}
